import { GeoLayer, UserCultureInfo } from '@InstanceFactory';
import clientStorage from '../Core/ClientStorage';
import Maybe from '../Core/Maybe';
import constants from '@Common/Core/Constant';
import FormatDotNetStyle from '@Libs/FormatDotNetStyle';
import { enumerable } from '@Common/Core/CoreDecorator';
import urlHelper from './UrlHelper';

export class LocalizationHelper {
    private static readonly defaultCultureInfo: UserCultureInfo = {
        locale: 'en-us',
        twoLetterISOLanguageName: 'en',
        numberDecimalSeparator: '.',
        numberGroupSeparator: ',',
        longTimePattern: constants.invariantCultureTimeFormat,
        longDatePattern: constants.invariantCultureDateFormat,
        shortTimePattern: constants.invariantCultureTimeFormat,
        shortDatePattern: constants.invariantCultureDateFormat,
        userName: 'English'
    };

    private monthNamesCache: string[];

    @enumerable
    public get userCultureInfo(): UserCultureInfo {
        return Maybe(altosoft.model)
            .read(m => m.user)
            .read(u => u.userCultureInfo)
            .valueOrDefault(LocalizationHelper.defaultCultureInfo);
    }

    public getTwoLettersLanguageName(): string {
        return this.userCultureInfo.twoLetterISOLanguageName;
    }

    public getLocaleForJQueryPicker(): string {
        if (altosoft.locale && altosoft.locale.toLowerCase() === 'zh-hans') {
            return 'zh-CN';
        }
        if (altosoft.locale && altosoft.locale.toLowerCase() === 'zh-hant') {
            return 'zh-TW';
        }
        return this.userCultureInfo.twoLetterISOLanguageName;
    }

    public getNumberDecimalSeparator(): string {
        return this.userCultureInfo.numberDecimalSeparator;
    }

    public getNumberGroupSeparator(): string {
        return this.userCultureInfo.numberGroupSeparator;
    }

    public getInputLongTimeFormat(withTime: boolean = true, timeOnly: boolean = false): string {
        const info = this.userCultureInfo;
        if (timeOnly) {
            return info.longTimePattern;
        } else {
            return withTime ? info.longDatePattern + ' ' + info.longTimePattern : info.longDatePattern;
        }
    }

    public getInputDateTimeFormat(withTime: boolean = true, timeOnly: boolean = false): string {
        const info = this.userCultureInfo;
        if (timeOnly) {
            return info.longTimePattern;
        } else {
            return withTime ? info.shortDatePattern + ' ' + info.shortTimePattern : info.shortDatePattern;
        }
    }

    public getInputTimeIntervalFormat(): string {
        return constants.invariantCultureTimeFormat;
    }

    @enumerable
    public get monthNames(): string[] {
        if (this.monthNamesCache) {
            return this.monthNamesCache;
        }
        const formatter = new FormatDotNetStyle(altosoft.locale);
        const fullMonthFormat = 'MMMM';
        return (this.monthNamesCache = _.range(0, 12, 1).map(month => formatter.formatDateTime(fullMonthFormat, new Date(1970, month))));
    }

    public changeLocale(locale: string): void {
        clientStorage.setLocale(locale);
        const parameters = {};
        parameters[`${altosoft.appId}_${constants.localeParameter}`] = locale;
        urlHelper.refresh(parameters);
    }

    private static readonly localizationGeoLayerMap = _.cond<GeoLayer, string>([
        [_.matches({ id: constants.geoLayers.countriesLayerId }), _.constant(strings.mapSettingsDialog.countries)],
        [_.matches({ id: constants.geoLayers.countiesLayerId }), _.constant(strings.mapSettingsDialog.counties)],
        [_.matches({ id: constants.geoLayers.statesLayerId }), _.constant(strings.mapSettingsDialog.states)],
        [_.matches({ id: constants.geoLayers.zipCodesLayerId }), _.constant(strings.mapSettingsDialog.zipCodes)],
        [_.stubTrue, g => g.name]
    ]);

    public localizeLayers(layers: GeoLayer[], copy = true): GeoLayer[] {
        const localized = copy ? _.cloneDeep(layers) : layers;

        for (const geoLayer of localized) {
            geoLayer.name = LocalizationHelper.localizationGeoLayerMap(geoLayer);
        }
        return localized;
    }
}

const localizationHelper = new LocalizationHelper();

export default localizationHelper;
